/**
 * Phase 14 — Admin Partner Lead Value Dashboard.
 *
 * Route: /admin/partners/lead-value (admin only)
 *
 * Surfaces 3 sections:
 *   1. Per-partner Lead Value Summary (clickable to filter the opp list)
 *   2. Opportunity Categories (counts + estimate bands + careful wording)
 *   3. Booking / Result Opportunity List (sortable by published_at)
 *      with per-row Status update flow (admin-only, audit-logged).
 *
 * WORDING SAFETY (pinned):
 *   • Every dollar value is shown as an indicative BAND with the
 *     literal text "Estimated opportunity value — not a quote".
 *   • BioCleanse appears only as a SUPPLEMENTARY note attached to
 *     microbiological / turbidity rows.
 *   • No "unsafe water" / "contaminated" / "must install" language.
 *   • No Soda Ash / RO recommendations.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, AlertTriangle, Loader2, Target,
  Users, ListChecks, DollarSign, ExternalLink, Filter, ShieldAlert,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";

const WINDOWS = [
  { id: "all",        label: "All time" },
  { id: "this_month", label: "This month" },
  { id: "last_30d",   label: "Last 30d" },
  { id: "last_7d",    label: "Last 7d" },
];

const STATUSES = [
  "new", "reviewed", "partner_notified_manually",
  "quoted", "won", "lost", "not_applicable",
];

const STATUS_TONES = {
  new:                       "bg-cyan-100 text-cyan-800",
  reviewed:                  "bg-violet-100 text-violet-800",
  partner_notified_manually: "bg-amber-100 text-amber-800",
  quoted:                    "bg-blue-100 text-blue-800",
  won:                       "bg-emerald-100 text-emerald-800",
  lost:                      "bg-slate-200 text-slate-700",
  not_applicable:            "bg-slate-100 text-slate-500",
};

const fmtMoney = (n) => (n === null || n === undefined || isNaN(n))
  ? "—"
  : `$${Math.round(Number(n) || 0).toLocaleString()}`;

const fmtNum = (n) => Number(n || 0).toLocaleString();

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU"); }
  catch { return iso; }
};

const fmtBand = (cat) => {
  if (!cat) return "—";
  if (cat.estimate_pending) return "Estimate pending";
  if (cat.estimate_low == null || cat.estimate_high == null) return "—";
  return `$${Math.round(cat.estimate_low).toLocaleString()}–$${Math.round(cat.estimate_high).toLocaleString()}`;
};


function WindowSelector({ window, onChange }) {
  return (
    <div
      className="inline-flex rounded-full bg-slate-100 p-1 text-xs font-semibold"
      data-testid="ph14-window-selector"
    >
      {WINDOWS.map(w => (
        <button
          key={w.id}
          onClick={() => onChange(w.id)}
          data-testid={`ph14-window-${w.id}`}
          className={`px-3 py-1 rounded-full transition ${
            window === w.id ? "bg-cyan-600 text-white shadow-sm" : "text-slate-600 hover:text-slate-800"
          }`}
        >
          {w.label}
        </button>
      ))}
    </div>
  );
}


// ── 1. Partner Lead Value Summary ───────────────────────────────────────
function PartnerLeadValueSummary({ data, selectedPartnerId, onSelect }) {
  const rows = data?.rows || [];
  const totals = data?.totals || {};
  return (
    <section
      data-testid="ph14-summary"
      className="rounded-2xl bg-white border border-slate-200 p-5"
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-cyan-700" />
          <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700">
            Partner Lead Value Summary
          </h3>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-600">
          <span data-testid="ph14-summary-total-est">
            <strong>{fmtMoney(totals.total_estimated_value)}</strong> est. value
          </span>
          <span data-testid="ph14-summary-total-opps">
            · <strong>{fmtNum(totals.total_opportunities)}</strong> opps
          </span>
          <span data-testid="ph14-summary-total-flagged">
            · <strong>{fmtNum(totals.total_flagged_results)}</strong> flagged results
          </span>
        </div>
      </div>
      {rows.length === 0 ? (
        <div
          data-testid="ph14-summary-empty"
          className="text-xs text-slate-400 italic py-2"
        >
          No partner activity in this window.
        </div>
      ) : (
        <div className="overflow-auto -mx-2">
          <table className="w-full text-xs" data-testid="ph14-summary-table">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left p-2">Partner</th>
                <th className="text-right p-2">Bookings</th>
                <th className="text-right p-2">Published</th>
                <th className="text-right p-2">Flagged</th>
                <th className="text-right p-2">Opps</th>
                <th className="text-right p-2">Est. lead value</th>
                <th className="text-right p-2">Avg / booking</th>
                <th className="text-left p-2">Last booking</th>
                <th className="text-left p-2">Last published</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr
                  key={r.partner_id}
                  data-testid={`ph14-summary-row-${r.partner_id}`}
                  onClick={() => onSelect(selectedPartnerId === r.partner_id ? null : r.partner_id)}
                  className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 transition ${
                    selectedPartnerId === r.partner_id ? "bg-cyan-50" : ""
                  }`}
                >
                  <td className="p-2">
                    <div className="font-semibold text-slate-800">
                      {r.business_name || <span className="text-slate-400 italic">(unknown)</span>}
                    </div>
                    <div className="font-mono text-[10px] text-slate-500">
                      {r.partner_slug || r.partner_id}
                    </div>
                  </td>
                  <td className="p-2 text-right">{fmtNum(r.total_bookings)}</td>
                  <td className="p-2 text-right">{fmtNum(r.published_results)}</td>
                  <td className="p-2 text-right">
                    {r.flagged_results > 0 ? (
                      <span className="text-amber-700 font-semibold">{fmtNum(r.flagged_results)}</span>
                    ) : fmtNum(r.flagged_results)}
                  </td>
                  <td className="p-2 text-right">{fmtNum(r.opportunities)}</td>
                  <td className="p-2 text-right font-bold">{fmtMoney(r.estimated_lead_value)}</td>
                  <td className="p-2 text-right">{fmtMoney(r.average_value_per_booking)}</td>
                  <td className="p-2">{fmtDate(r.last_booking_at)}</td>
                  <td className="p-2">{fmtDate(r.last_published_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p
        data-testid="ph14-summary-disclaimer"
        className="mt-3 text-[10px] text-slate-400 italic"
      >
        {data?.disclaimer || "Estimated opportunity values are indicative bands only — not quotes."}
      </p>
    </section>
  );
}


// ── 2. Opportunity Categories ───────────────────────────────────────────
function OpportunityCategories({ data }) {
  const cats = data?.categories || [];
  return (
    <section
      data-testid="ph14-categories"
      className="rounded-2xl bg-white border border-slate-200 p-5"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-cyan-700" />
          <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700">
            Opportunity Categories
          </h3>
        </div>
        <div className="text-xs text-slate-600">
          Total: <strong>{fmtNum(data?.total)}</strong>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cats.map(c => (
          <div
            key={c.slug}
            data-testid={`ph14-category-${c.slug}`}
            className="rounded-lg border border-slate-100 bg-slate-50 p-3"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold text-sm text-slate-800">{c.label}</div>
              <div className="text-xs font-bold px-2 py-0.5 rounded-full bg-white border border-slate-200">
                {fmtNum(c.count)}
              </div>
            </div>
            <div className="text-[11px] text-slate-600 mt-1">{c.trigger_summary}</div>
            <div className="mt-2 text-xs">
              <span className="font-semibold text-slate-700">{fmtBand(c)}</span>
              <span className="ml-1 text-slate-400 italic">— estimated opportunity value, not a quote</span>
            </div>
            <div className="mt-2 text-[11px] text-slate-700">{c.recommended_action_text}</div>
            {c.biocleanse_note && (
              <div
                data-testid={`ph14-category-biocleanse-${c.slug}`}
                className="mt-2 text-[11px] text-violet-700 italic"
              >
                {c.biocleanse_note}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}


// ── 3. Opportunity List ─────────────────────────────────────────────────
function OpportunityList({ data, partnerId, statusFilter, onStatusFilter, onStatusChange, busy }) {
  const rows = data?.rows || [];
  return (
    <section
      data-testid="ph14-opportunities"
      className="rounded-2xl bg-white border border-slate-200 p-5"
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-cyan-700" />
          <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700">
            Booking / Result Opportunities
          </h3>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {partnerId && (
            <span
              data-testid="ph14-opportunities-partner-filter-pill"
              className="px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800 font-semibold"
            >
              partner_id = {partnerId.slice(0, 8)}…
            </span>
          )}
          <Filter className="w-3 h-3 text-slate-400" />
          <select
            value={statusFilter || ""}
            onChange={(e) => onStatusFilter(e.target.value || null)}
            data-testid="ph14-opportunities-status-filter"
            className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white"
          >
            <option value="">All statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}
          </select>
          <span className="text-slate-500">
            {data?.showing ?? rows.length} / {data?.count ?? rows.length}
          </span>
        </div>
      </div>
      {rows.length === 0 ? (
        <div
          data-testid="ph14-opportunities-empty"
          className="text-xs text-slate-400 italic py-4 text-center"
        >
          No opportunities match these filters.
        </div>
      ) : (
        <div className="overflow-auto -mx-2">
          <table className="w-full text-xs" data-testid="ph14-opportunities-table">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left p-2">Booking</th>
                <th className="text-left p-2">Customer</th>
                <th className="text-left p-2">Category</th>
                <th className="text-left p-2">Main finding</th>
                <th className="text-left p-2">Published</th>
                <th className="text-right p-2">Est. value</th>
                <th className="text-left p-2">Status</th>
                <th className="text-right p-2">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr
                  key={r.opportunity_id}
                  data-testid={`ph14-opportunities-row-${r.opportunity_id}`}
                  className="border-t border-slate-100 align-top"
                >
                  <td className="p-2">
                    <Link
                      to={`/admin/bookings/${r.booking_reference}`}
                      data-testid={`ph14-opp-booking-link-${r.opportunity_id}`}
                      className="text-cyan-700 hover:underline font-mono"
                    >{r.booking_reference}</Link>
                    <div className="text-[10px] text-slate-500 font-mono">{r.partner_slug || r.partner_id}</div>
                  </td>
                  <td className="p-2">
                    <div className="text-slate-800">{r.customer_name || "—"}</div>
                    <div className="text-[10px] text-slate-500">{r.customer_suburb || ""} {r.customer_postcode || ""}</div>
                  </td>
                  <td className="p-2">
                    <div className="font-semibold text-slate-800">{r.category?.label || r.category_slug}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5 max-w-[260px]">
                      {r.category?.recommended_action_text || ""}
                    </div>
                    {r.category?.biocleanse_note && (
                      <div className="text-[10px] text-violet-700 italic mt-1 max-w-[260px]">
                        {r.category.biocleanse_note}
                      </div>
                    )}
                  </td>
                  <td className="p-2 max-w-[200px] text-slate-700">{r.main_finding}</td>
                  <td className="p-2">{fmtDate(r.published_at)}</td>
                  <td className="p-2 text-right">
                    <div className="font-bold text-slate-800">{fmtBand(r.category)}</div>
                    <div className="text-[10px] text-slate-400 italic">not a quote</div>
                  </td>
                  <td className="p-2">
                    <select
                      value={r.status}
                      disabled={busy === r.opportunity_id}
                      onChange={(e) => onStatusChange(r, e.target.value)}
                      data-testid={`ph14-opp-status-select-${r.opportunity_id}`}
                      className={`text-[11px] border border-slate-200 rounded-md px-1.5 py-0.5 ${STATUS_TONES[r.status] || ""}`}
                    >
                      {STATUSES.map(s => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}
                    </select>
                    {r.status_updated_at && (
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {fmtDate(r.status_updated_at)}{r.status_updated_by ? ` · ${r.status_updated_by}` : ""}
                      </div>
                    )}
                  </td>
                  <td className="p-2 text-right">
                    {r.passport_test_id && (
                      <Link
                        to={`/admin/lab/tests/${r.passport_test_id}`}
                        data-testid={`ph14-opp-passport-link-${r.opportunity_id}`}
                        className="text-[11px] text-cyan-700 hover:underline inline-flex items-center gap-1"
                      >
                        Water Record <ExternalLink className="w-3 h-3" />
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p
        data-testid="ph14-opportunities-disclaimer"
        className="mt-3 text-[10px] text-slate-400 italic"
      >
        {data?.disclaimer || "Estimated opportunity values are indicative bands only — not quotes."}
      </p>
    </section>
  );
}


// ── Recommended Follow-Ups Summary Card (Phase 15) ─────────────────────
function FollowUpsSummaryCard({ window }) {
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/pws-follow-ups/summary", {
          params: { window },
        });
        if (alive) setSummary(data);
      } catch (e) {
        if (alive) setErr(formatApiError(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [window]);

  const total = summary?.total ?? 0;
  const newCount = summary?.by_status?.new ?? 0;
  const highCount = summary?.by_severity?.high ?? 0;
  const actionedCount =
    (summary?.by_status?.reviewed ?? 0) +
    (summary?.by_status?.manually_assigned ?? 0) +
    (summary?.by_status?.partner_contacted_manually ?? 0) +
    (summary?.by_status?.customer_contacted_manually ?? 0) +
    (summary?.by_status?.quoted ?? 0) +
    (summary?.by_status?.completed ?? 0);

  return (
    <section
      data-testid="ph14-followups-summary-card"
      className="rounded-2xl bg-white border border-violet-200 p-5"
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-violet-700" />
          <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700">
            Recommended Follow-Up Actions
          </h3>
        </div>
        <Link
          to="/admin/partners/follow-ups"
          data-testid="ph14-followups-summary-link"
          className="text-xs font-semibold text-violet-700 hover:text-violet-900 inline-flex items-center gap-1"
        >
          Open Follow-Ups <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      {err ? (
        <div
          data-testid="ph14-followups-summary-error"
          className="text-xs text-rose-700 italic"
        >
          {err}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div
            data-testid="ph14-followups-summary-total"
            className="rounded-lg p-3 bg-slate-50 border border-slate-200"
          >
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              Total
            </div>
            <div className="mt-1 text-lg font-bold">{fmtNum(total)}</div>
          </div>
          <div
            data-testid="ph14-followups-summary-new"
            className="rounded-lg p-3 bg-cyan-50 border border-cyan-100"
          >
            <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-700">
              New
            </div>
            <div className="mt-1 text-lg font-bold text-cyan-900">
              {fmtNum(newCount)}
            </div>
          </div>
          <div
            data-testid="ph14-followups-summary-high"
            className="rounded-lg p-3 bg-rose-50 border border-rose-100"
          >
            <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700">
              High priority
            </div>
            <div className="mt-1 text-lg font-bold text-rose-900">
              {fmtNum(highCount)}
            </div>
          </div>
          <div
            data-testid="ph14-followups-summary-actioned"
            className="rounded-lg p-3 bg-emerald-50 border border-emerald-100"
          >
            <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">
              Reviewed / actioned
            </div>
            <div className="mt-1 text-lg font-bold text-emerald-900">
              {fmtNum(actionedCount)}
            </div>
          </div>
        </div>
      )}
      <p className="mt-3 text-[10px] text-slate-400 italic">
        Recommended follow-up actions are admin-managed review suggestions only — no
        automated emails, no work orders, no quotes are sent.
      </p>
    </section>
  );
}


// ── Page ────────────────────────────────────────────────────────────────
export default function AdminPWSLeadValue() {
  const [window, setWindow] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState(null);
  const [opportunities, setOpportunities] = useState(null);
  const [partnerFilter, setPartnerFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [busy, setBusy] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, c, o] = await Promise.all([
        api.get("/admin/pws-lead-value/summary",      { params: { window } }),
        api.get("/admin/pws-lead-value/categories",   { params: { window } }),
        api.get("/admin/pws-lead-value/opportunities", {
          params: {
            window,
            ...(partnerFilter ? { partner_id: partnerFilter } : {}),
            ...(statusFilter ? { status: statusFilter } : {}),
            limit: 200,
          },
        }),
      ]);
      setSummary(s.data);
      setCategories(c.data);
      setOpportunities(o.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [s, c, o] = await Promise.all([
          api.get("/admin/pws-lead-value/summary",      { params: { window } }),
          api.get("/admin/pws-lead-value/categories",   { params: { window } }),
          api.get("/admin/pws-lead-value/opportunities", {
            params: {
              window,
              ...(partnerFilter ? { partner_id: partnerFilter } : {}),
              ...(statusFilter ? { status: statusFilter } : {}),
              limit: 200,
            },
          }),
        ]);
        if (!alive) return;
        setSummary(s.data);
        setCategories(c.data);
        setOpportunities(o.data);
      } catch (e) {
        if (alive) setError(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [window, partnerFilter, statusFilter]);

  const handleStatusChange = async (row, newStatus) => {
    setBusy(row.opportunity_id);
    try {
      const { data } = await api.patch(
        `/admin/pws-lead-value/opportunities/${encodeURIComponent(row.booking_reference)}/${encodeURIComponent(row.category_slug)}/status`,
        { status: newStatus, note: null },
      );
      // Optimistically refresh the row in local state
      setOpportunities((cur) => cur ? ({
        ...cur,
        rows: cur.rows.map(r =>
          r.opportunity_id === row.opportunity_id
            ? { ...r, status: newStatus,
                status_updated_at: data?.row?.updated_at || r.status_updated_at,
                status_updated_by: data?.row?.updated_by || r.status_updated_by,
                audit_log: data?.row?.audit_log || r.audit_log }
            : r),
      }) : cur);
      toast.success(`Status → ${newStatus.replaceAll("_", " ")}`);
    } catch (e) {
      toast.error(formatApiError(e) || "Status update failed");
    } finally {
      setBusy(null);
    }
  };

  const headerTotals = useMemo(() => summary?.totals || {}, [summary]);

  return (
    <PortalShell>
      <div
        data-testid="admin-pws-lead-value-page"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      >
        <Link
          to="/admin/partners/network"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-700 hover:text-cyan-800"
          data-testid="ph14-back-to-network"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to PWS Network
        </Link>

        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">
              Aware Partner Network · Phase 14
            </div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">
              Partner Lead Value
            </h1>
            <p className="text-slate-500 mt-1 text-sm max-w-3xl">
              The partner network creates more than just test bookings — every
              flagged result is a potential corrective-service opportunity for a
              PWS operator. This dashboard estimates indicative lead value by
              partner, by category, and by booking.{" "}
              <strong className="text-slate-700">
                Estimates are indicative bands only — not quotes and not
                guaranteed revenue.
              </strong>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <WindowSelector window={window} onChange={setWindow} />
            <button
              onClick={loadAll}
              disabled={loading}
              data-testid="ph14-refresh-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-60"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
        </div>

        {/* Safety banner — careful wording */}
        <div
          data-testid="ph14-safety-banner"
          className="mt-5 rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3 text-sm text-amber-900"
        >
          <ShieldAlert className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Wording boundary:</strong> Use phrases like
            <em> &ldquo;does not meet ADWG&rdquo;</em>,
            <em> &ldquo;recommended follow-up&rdquo;</em>,
            <em> &ldquo;potential corrective action&rdquo;</em>. Avoid medical
            claims (unsafe water, contamination making someone sick, &ldquo;must
            install&rdquo;). BioCleanse is offered as a supplementary
            recommendation only — never a replacement for tank cleaning,
            filtration or UV. Soda Ash and Reverse Osmosis are NOT default
            recommendations.
          </div>
        </div>

        {/* Top stat strip */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div data-testid="ph14-stat-partners" className="rounded-lg p-3 bg-white border border-slate-200">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Partners</div>
            <div className="mt-1 text-lg font-bold">{fmtNum(headerTotals.partner_count)}</div>
          </div>
          <div data-testid="ph14-stat-opps" className="rounded-lg p-3 bg-white border border-slate-200">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Opportunities</div>
            <div className="mt-1 text-lg font-bold">{fmtNum(headerTotals.total_opportunities)}</div>
          </div>
          <div data-testid="ph14-stat-flagged" className="rounded-lg p-3 bg-white border border-slate-200">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Flagged results</div>
            <div className="mt-1 text-lg font-bold">{fmtNum(headerTotals.total_flagged_results)}</div>
          </div>
          <div data-testid="ph14-stat-value" className="rounded-lg p-3 bg-emerald-50 border border-emerald-100">
            <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 flex items-center gap-1">
              <DollarSign className="w-3 h-3" /> Est. lead value
            </div>
            <div className="mt-1 text-lg font-bold text-emerald-900">{fmtMoney(headerTotals.total_estimated_value)}</div>
          </div>
        </div>

        {loading ? (
          <div
            data-testid="ph14-loading"
            className="mt-8 rounded-2xl bg-white border border-slate-200 p-10 flex items-center gap-2 text-slate-400 justify-center"
          >
            <Loader2 className="w-5 h-5 animate-spin" /> Loading lead value…
          </div>
        ) : error ? (
          <div
            data-testid="ph14-error"
            className="mt-8 rounded-xl bg-rose-50 border border-rose-200 p-5 text-sm text-rose-800"
          >
            <div className="font-semibold mb-1 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Failed to load
            </div>
            <div className="text-xs">{error}</div>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-4">
            <PartnerLeadValueSummary
              data={summary}
              selectedPartnerId={partnerFilter}
              onSelect={setPartnerFilter}
            />
            <FollowUpsSummaryCard window={window} />
            <OpportunityCategories data={categories} />
            <OpportunityList
              data={opportunities}
              partnerId={partnerFilter}
              statusFilter={statusFilter}
              onStatusFilter={setStatusFilter}
              onStatusChange={handleStatusChange}
              busy={busy}
            />
          </div>
        )}
      </div>
    </PortalShell>
  );
}
